/** @format */

import React, { useState, useEffect } from "react";
import { Helmet } from "fusion-plugin-react-helmet-async";
import { useForm } from "react-hook-form";
import Switch from "react-switch";
import useBuyStore from "../store/BuyStore";
import QuoteFormWrapper from "../../../shared/components/quoteFormWrapper";
import QuoteStepHeading from "../../../shared/components/QuoteStepHeading";
import QuoteStepLoader from "../../../shared/components/QuoteStepLoader";
import FormNavigationButtons from "../../../shared/components/FormNavigationButtons";
import BuyStep from "../enums/BuyStep";
import { handleNext } from "../../../shared/utils/navigationUtils";
import TbEPaymentsMapper from "../mappers/tbEPaymentsMapper";
import { useNavigate } from "fusion-plugin-react-router";
import { fetchPaymentJson } from "../services/buyService";
import PaymentsAccordion from "../../../shared/components/paymentsAccordion";
import {
  TAXI_FINANCE_INTEREST,
  TAXI_FINANCE_APR,
  TAXI_QUOTE_BRANDREF,
} from "../../../config/config";
import useAppStateStore from "../../../shared/store/AppStateStore";
import { HotjarEventNames } from "../../../shared/utils/hotjarEventNames";
import { TrackHotjarEvent } from "../../../shared/utils/trackHotjarEvent";
import useQuoteStore from "../../quote/store/QuoteStore";

const TbEPayment = () => {
  const [selectedOption, setSelectedOption] = useState(null);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const buyStore = useBuyStore.getState();
  const navigate = useNavigate();
  const brandRef = TAXI_QUOTE_BRANDREF;
  // Initialize model
  const tbEPaymentsData = TbEPaymentsMapper.get();

  // Form Setup
  const { setValue, handleSubmit } = useForm({
    defaultValues: tbEPaymentsData,
  });

  let STATE = useAppStateStore();

  const handleSelectOption = (option) => {
    if (option === selectedOption) {
      setSelectedOption(null);
      setValue("selectedOption", null);
      return;
    }

    if (
      (option === "monthly" && !tbEPaymentsData.paymentOptionsMonthly) ||
      (option === "annual" && !tbEPaymentsData.paymentOptionsAnnually)
    ) {
      return;
    }
    setSelectedOption(option);
    setValue("selectedOption", option);
  };

  useEffect(() => {
    TrackHotjarEvent(HotjarEventNames.TAXI_QUOTE_TBEPAYMENTS_ENTERED);
  }, []);

  const onSubmit = async (data) => {
    if (!data.selectedOption) return;

    // Set the selected payment option in buyStore
    TrackHotjarEvent(HotjarEventNames.TAXI_QUOTE_TBEPAYMENTS_NEXT_CLICKED);
    buyStore.setObject(
      "selectedPaymentOption",
      data.selectedOption === "monthly"
        ? tbEPaymentsData.paymentOptionsMonthly
        : tbEPaymentsData.paymentOptionsAnnually,
    );

    setPaymentLoading(true);
    try {
      const selectedPaymentOption = buyStore.getObject("selectedPaymentOption");
      const riskObjectId =
        useQuoteStore.getState().quote?.risk?.riskObjectId ?? "";

      const requestBody = {
        brandRef: brandRef,
        RiskObjectId: riskObjectId,
        RiskObjectHistoryId: 1,
        CardPaymentAmount:
          selectedPaymentOption?.deposit ||
          selectedPaymentOption?.totalAmountToPayByCustomer ||
          5000,
        SelectedPaymentPlanId: selectedPaymentOption?.paymentOptionId,
      };

      const jsonData = await fetchPaymentJson(requestBody, navigate);
      setValue("jsonPaymentData", jsonData);
      buyStore.setObject("jsonPaymentData", jsonData);
      if (data.selectedOption === "annual") {
        handleNext(BuyStep.TB_GLOBAL_PAYMENTS_PAGE, STATE);
      } else if (data.selectedOption === "monthly") {
        handleNext(BuyStep.TB_DIRECT_DEBIT_DETAILS, STATE);
      }
    } catch (error) {
      navigate(
        `/error?message=${encodeURIComponent(error.message) ||
          "Internal Server Error"}`,
      );
      console.error("Error fetching payment JSON:", error);
    } finally {
      setPaymentLoading(false);
    }
  };

  if (paymentLoading || STATE.paymentsLoading) {
    return (
      <QuoteStepLoader
        title="Select your payment option"
        description="You're nearly there! Select whether you would like to pay monthly or annually for your cover."
        message="Selecting your payment option..."
      />
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Helmet>
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=0"
        />
      </Helmet>

      <QuoteFormWrapper>
        <div className="tw-flex tw-flex-col tw-gap-8">
          <section>
            <QuoteStepHeading
              title="Price Breakdown"
              descriptionClassName="tw-hidden"
            />

            <div className="tw-mt-4 tw-flex tw-w-full tw-flex-col tw-gap-3 tw-rounded-dis-card tw-border tw-border-solid tw-border-dis-border-strong tw-bg-white tw-p-4 sm:tw-p-5">
              {/* Gross Breakdown */}
              <PaymentsAccordion
                title="Policy"
                headers={["Premium (Excl. IPT)", "IPT", "Premium (Incl. IPT)"]}
                data={[
                  `£
                    ${tbEPaymentsData.quoteBreakdown?.insurerGrossExcIPT.toLocaleString(
                      "en-GB",
                      { minimumFractionDigits: 2, maximumFractionDigits: 2 },
                    )}`,
                  `£
                    ${tbEPaymentsData.quoteBreakdown?.insurerIPT.toLocaleString(
                      "en-GB",
                      {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      },
                    )}
                    `,
                  `£
                    ${tbEPaymentsData.quoteBreakdown?.insurerGrossIncIPT.toLocaleString(
                      "en-GB",
                      { minimumFractionDigits: 2, maximumFractionDigits: 2 },
                    )}
                    `,
                ]}
              />

              {/* Add-Ons Section */}
              {Array.isArray(tbEPaymentsData.quoteBreakdown?.addonsQuoted) &&
                tbEPaymentsData.quoteBreakdown.addonsQuoted.length > 0 && (
                  <PaymentsAccordion
                    title="Add-Ons (Incl. IPT)"
                    headers={tbEPaymentsData.quoteBreakdown.addonsQuoted.map(
                      (addon) => `${addon.addonSchemeName}`,
                    )}
                    data={tbEPaymentsData.quoteBreakdown.addonsQuoted.map(
                      (addon) =>
                        `£${addon.addonGrossPremiumIncIPT.toLocaleString(
                          "en-GB",
                          {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          },
                        )}`,
                    )}
                  />
                )}

              {/* Admin Fee Section */}
              <PaymentsAccordion
                title="Our Costs"
                headers={["Policy Arrangement Fee"]}
                data={[
                  `£${tbEPaymentsData.quoteBreakdown?.adminFee.toLocaleString(
                    "en-GB",
                    {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    },
                  )}`,
                ]}
              />

              {/* Total Section */}
              <div className="tw-flex tw-items-center tw-justify-between tw-gap-4 tw-rounded-lg tw-bg-dis-field tw-p-3 tw-text-base tw-text-dis-navy">
                <span className="tw-font-semibold">Total</span>
                <span className="tw-font-bold">
                  £
                  {tbEPaymentsData.quoteBreakdown?.quotedTotal.toLocaleString(
                    "en-GB",
                    { minimumFractionDigits: 2, maximumFractionDigits: 2 },
                  )}
                </span>
              </div>
            </div>
          </section>

          <section>
            <QuoteStepHeading
              className="tw-mb-4"
              titleClassName="tw-mb-4"
              title="Select your payment option"
              description="You're nearly there! Select whether you would like to pay monthly or annually for your cover."
            />

            {tbEPaymentsData.financeAvailable && (
              <p className="tw-mb-3 tw-text-dis-small tw-text-dis-text-muted">
                Paying monthly will cost you £{tbEPaymentsData.serviceCharge}{" "}
                more than paying annually.
              </p>
            )}

            <p className="tw-mb-4 tw-text-dis-small tw-leading-[1.5] tw-text-dis-text-muted">
              Your monthly installments include a {TAXI_FINANCE_INTEREST}%
              interest charge which is equal to an APR of {TAXI_FINANCE_APR}%.
            </p>

            {/* Monthly Payment Option */}
            {tbEPaymentsData.financeAvailable && (
              <div
                role="button"
                tabIndex={0}
                aria-pressed={selectedOption === "monthly"}
                className={`tw-mb-4 tw-w-full tw-cursor-pointer tw-rounded-dis-card tw-border-solid tw-transition-colors ${
                  selectedOption === "monthly"
                    ? "tw-border-2 tw-border-dis-deep-blue tw-bg-dis-sky-tint tw-p-[19px]"
                    : "tw-border tw-border-dis-border-strong tw-bg-white tw-p-5 hover:tw-border-dis-deep-blue"
                }`}
                onClick={() => handleSelectOption("monthly")}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    handleSelectOption("monthly");
                  }
                }}
              >
                {tbEPaymentsData.paymentOptionsMonthly?.installmentAmount ? (
                  <>
                    <div className="tw-flex tw-items-center tw-justify-between tw-gap-4">
                      <div className="tw-min-w-0">
                        <p className="tw-m-0 tw-text-3xl tw-font-bold tw-leading-tight tw-text-dis-navy">
                          £
                          {
                            tbEPaymentsData.paymentOptionsMonthly
                              .installmentAmount
                          }
                        </p>
                      </div>

                      <Switch
                        width={50}
                        height={30}
                        handleDiameter={26}
                        offColor="#EAF1F6"
                        onColor="#0B6694"
                        checkedIcon={false}
                        uncheckedIcon={false}
                        checked={selectedOption === "monthly"}
                        onChange={() => handleSelectOption("monthly")}
                      />
                    </div>

                    <p className="tw-mb-2 tw-mt-3 tw-text-base tw-font-normal tw-text-dis-navy">
                      Pay Monthly
                    </p>
                    <p className="tw-m-0 tw-text-base tw-leading-[1.5] tw-text-dis-text-2">
                      <strong className="tw-font-bold">
                        £{tbEPaymentsData.paymentOptionsMonthly.deposit}
                      </strong>{" "}
                      deposit followed by{" "}
                      <span className="tw-font-semibold">
                        {tbEPaymentsData.paymentOptionsMonthly.installments}
                      </span>{" "}
                      installments of{" "}
                      <strong className="tw-font-bold">
                        £
                        {
                          tbEPaymentsData.paymentOptionsMonthly
                            .installmentAmount
                        }
                      </strong>{" "}
                      per month
                    </p>
                  </>
                ) : (
                  <p className="tw-m-0 tw-text-base tw-text-dis-text-muted">
                    Couldn't get your monthly payments plan.. Please try again
                    later.
                  </p>
                )}
              </div>
            )}

            {/* Annual Payment Option */}
            <div
              role="button"
              tabIndex={0}
              aria-pressed={selectedOption === "annual"}
              className={`tw-mb-4 tw-w-full tw-cursor-pointer tw-rounded-dis-card tw-border-solid tw-transition-colors ${
                selectedOption === "annual"
                  ? "tw-border-2 tw-border-dis-deep-blue tw-bg-dis-sky-tint tw-p-[19px]"
                  : "tw-border tw-border-dis-border-strong tw-bg-white tw-p-5 hover:tw-border-dis-deep-blue"
              }`}
              onClick={() => handleSelectOption("annual")}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  handleSelectOption("annual");
                }
              }}
            >
              {tbEPaymentsData.paymentOptionsAnnually
                ?.totalAmountToPayByCustomer ? (
                <>
                  <div className="tw-flex tw-items-center tw-justify-between tw-gap-4">
                    <p className="tw-m-0 tw-text-3xl tw-font-bold tw-leading-tight tw-text-dis-navy">
                      £
                      {tbEPaymentsData.paymentOptionsAnnually.totalAmountToPayByCustomer.toLocaleString(
                        "en-GB",
                        {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        },
                      )}
                    </p>
                    <Switch
                      width={50}
                      height={30}
                      handleDiameter={26}
                      offColor="#EAF1F6"
                      onColor="#0B6694"
                      checkedIcon={false}
                      uncheckedIcon={false}
                      checked={selectedOption === "annual"}
                      onChange={() => handleSelectOption("annual")}
                    />
                  </div>

                  <p className="tw-mb-2 tw-mt-3 tw-text-base tw-font-normal tw-text-dis-navy">
                    Pay Annually
                  </p>
                  <p className="tw-m-0 tw-text-base tw-leading-[1.5] tw-text-dis-text-2">
                    One off payment
                  </p>
                </>
              ) : (
                <p className="tw-m-0 tw-text-base tw-text-dis-text-muted">
                  Couldn't get your yearly payments plan.. Please try again
                  later.
                </p>
              )}
            </div>
          </section>
        </div>
      </QuoteFormWrapper>

      <FormNavigationButtons
        prevStep={BuyStep.TB_DOCUMENTS}
        isValid={selectedOption}
      />
    </form>
  );
};

export default TbEPayment;
